/**
 * EditorToolbar — Full-featured formatting toolbar for the BlockNote page editor.
 *
 * Groups: Headings | Text styles | Lists | Block types (dropdown) | Alignment (dropdown) | Insert | Save
 */

import React, { useCallback, useState, useRef, useEffect } from 'react';
import type { BlockNoteEditor } from '@blocknote/core';
import { PageLinkPicker } from './PageLinkPicker';
import { PagePresence } from './PagePresence';
import * as pagesApi from '../../api/pages-api';

interface EditorToolbarProps {
  readonly editor: BlockNoteEditor<any, any, any> | null;
  readonly pageId: string;
  readonly hasUnsavedChanges: boolean;
  readonly saving: boolean;
  readonly onSave: () => void;
}

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

interface ToolbarButtonProps {
  readonly label: React.ReactNode;
  readonly title: string;
  readonly onClick: () => void;
  readonly active?: boolean;
  readonly disabled?: boolean;
}

function ToolbarButton({ label, title, onClick, active, disabled }: ToolbarButtonProps): React.ReactElement {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={`px-1.5 py-1 text-sm font-medium rounded transition-colors
        ${active ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'}
        ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
      `}
    >
      {label}
    </button>
  );
}

function ToolbarSeparator(): React.ReactElement {
  return <div className="w-px h-5 bg-border mx-1" role="separator" />;
}

// ---------------------------------------------------------------------------
// Dropdown menu for grouping related actions
// ---------------------------------------------------------------------------

interface DropdownItem {
  readonly label: string;
  readonly title: string;
  readonly onClick: () => void;
}

interface ToolbarDropdownProps {
  readonly buttonLabel: string;
  readonly buttonTitle: string;
  readonly items: DropdownItem[];
  readonly disabled?: boolean;
}

function ToolbarDropdown({ buttonLabel, buttonTitle, items, disabled }: ToolbarDropdownProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        title={buttonTitle}
        aria-label={buttonTitle}
        aria-expanded={open}
        aria-haspopup="menu"
        disabled={disabled}
        onClick={() => setOpen(!open)}
        className={`px-1.5 py-1 text-sm font-medium rounded transition-colors flex items-center gap-0.5
          ${open ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'}
          ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
        `}
      >
        {buttonLabel}
        <svg className="w-3 h-3" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
          <path d="M3 5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" fill="none" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute top-full left-0 mt-1 z-50 min-w-[160px] rounded-md border bg-popover shadow-md py-1"
          role="menu"
        >
          {items.map((item) => (
            <button
              key={item.title}
              type="button"
              role="menuitem"
              className="w-full text-left px-3 py-1.5 text-sm text-foreground hover:bg-accent transition-colors"
              onClick={() => {
                item.onClick();
                setOpen(false);
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Color picker dropdown for text/highlight colors
// ---------------------------------------------------------------------------

const COLOR_OPTIONS = [
  { value: 'default', label: 'Default', swatch: 'transparent' },
  { value: 'red', label: 'Red', swatch: '#e03e3e' },
  { value: 'orange', label: 'Orange', swatch: '#d9730d' },
  { value: 'yellow', label: 'Yellow', swatch: '#dfab01' },
  { value: 'green', label: 'Green', swatch: '#4d6461' },
  { value: 'blue', label: 'Blue', swatch: '#0b6e99' },
  { value: 'purple', label: 'Purple', swatch: '#6940a5' },
  { value: 'pink', label: 'Pink', swatch: '#ad1a72' },
  { value: 'gray', label: 'Gray', swatch: '#9b9a97' },
  { value: 'brown', label: 'Brown', swatch: '#64473a' },
];

interface TextColorDropdownProps {
  readonly buttonLabel: string;
  readonly buttonTitle: string;
  readonly onSelect: (color: string) => void;
  readonly disabled?: boolean;
}

function TextColorDropdown({ buttonLabel, buttonTitle, onSelect, disabled }: TextColorDropdownProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        title={buttonTitle}
        aria-label={buttonTitle}
        aria-expanded={open}
        aria-haspopup="true"
        disabled={disabled}
        onClick={() => setOpen(!open)}
        className={`px-1.5 py-1 text-sm font-medium rounded transition-colors
          ${open ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'}
          ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
        `}
      >
        {buttonLabel}
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 rounded-md border bg-popover shadow-md p-2 grid grid-cols-3 gap-1 min-w-[140px]">
          {COLOR_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              title={opt.label}
              aria-label={opt.label}
              className="w-7 h-7 rounded border border-border hover:ring-2 hover:ring-primary/50 transition-all flex items-center justify-center"
              style={{ backgroundColor: opt.swatch === 'transparent' ? undefined : opt.swatch }}
              onClick={() => {
                onSelect(opt.value);
                setOpen(false);
              }}
            >
              {opt.value === 'default' && (
                <span className="text-xs text-muted-foreground">✕</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Table size picker — Word-style NxM grid selector
// ---------------------------------------------------------------------------

interface TableSizePickerProps {
  readonly onSelect: (rows: number, cols: number) => void;
  readonly disabled?: boolean;
}

function TableSizePicker({ onSelect, disabled }: TableSizePickerProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [hoverRow, setHoverRow] = useState(0);
  const [hoverCol, setHoverCol] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const maxRows = 8;
  const maxCols = 8;

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        title="Insert table"
        aria-label="Insert table"
        aria-expanded={open}
        aria-haspopup="true"
        disabled={disabled}
        onClick={() => setOpen(!open)}
        className={`px-1.5 py-1 text-sm font-medium rounded transition-colors
          ${open ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'}
          ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
        `}
      >
        ⊞
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 rounded-md border bg-popover shadow-md p-2">
          <p className="text-xs text-muted-foreground mb-1.5 text-center">
            {hoverRow > 0 ? `${hoverRow} × ${hoverCol}` : 'Select table size'}
          </p>
          <div
            className="grid gap-0.5"
            style={{ gridTemplateColumns: `repeat(${maxCols}, 1fr)` }}
            onMouseLeave={() => { setHoverRow(0); setHoverCol(0); }}
          >
            {Array.from({ length: maxRows * maxCols }, (_, idx) => {
              const row = Math.floor(idx / maxCols) + 1;
              const col = (idx % maxCols) + 1;
              const isHighlighted = row <= hoverRow && col <= hoverCol;
              return (
                <button
                  key={idx}
                  type="button"
                  className={`w-4 h-4 border rounded-sm transition-colors ${
                    isHighlighted
                      ? 'bg-primary/30 border-primary/50'
                      : 'bg-muted/30 border-border hover:border-primary/30'
                  }`}
                  onMouseEnter={() => { setHoverRow(row); setHoverCol(col); }}
                  onClick={() => {
                    onSelect(row, col);
                    setOpen(false);
                    setHoverRow(0);
                    setHoverCol(0);
                  }}
                  aria-label={`${row} rows × ${col} columns`}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main toolbar
// ---------------------------------------------------------------------------

export function EditorToolbar({ editor, pageId, hasUnsavedChanges, saving, onSave }: EditorToolbarProps): React.ReactElement {
  // --- Headings ---
  const toggleHeading = useCallback(
    (level: 1 | 2 | 3 | 4) => {
      if (!editor) return;
      const block = editor.getTextCursorPosition().block;
      if (block.type === 'heading' && (block.props as any).level === level) {
        editor.updateBlock(block, { type: 'paragraph' });
      } else {
        editor.updateBlock(block, { type: 'heading', props: { level } as any });
      }
      editor.focus();
    },
    [editor]
  );

  // --- Inline styles ---
  const toggleStyle = useCallback(
    (style: 'bold' | 'italic' | 'code' | 'underline' | 'strike') => {
      if (!editor) return;
      editor.toggleStyles({ [style]: true });
      editor.focus();
    },
    [editor]
  );

  // --- Lists ---
  const toggleList = useCallback(
    (listType: 'bulletListItem' | 'numberedListItem' | 'checkListItem') => {
      if (!editor) return;
      const block = editor.getTextCursorPosition().block;
      if (block.type === listType) {
        editor.updateBlock(block, { type: 'paragraph' });
      } else {
        editor.updateBlock(block, { type: listType } as any);
      }
      editor.focus();
    },
    [editor]
  );

  // --- Block types ---
  const setBlockType = useCallback(
    (blockType: string) => {
      if (!editor) return;
      const block = editor.getTextCursorPosition().block;
      if (block.type === blockType) {
        editor.updateBlock(block, { type: 'paragraph' });
      } else {
        editor.updateBlock(block, { type: blockType } as any);
      }
      editor.focus();
    },
    [editor]
  );

  const insertTable = useCallback((rows: number, cols: number) => {
    if (!editor) return;
    const block = editor.getTextCursorPosition().block;
    const tableRows = Array.from({ length: rows }, () => ({
      cells: Array.from({ length: cols }, () => []),
    }));
    editor.insertBlocks(
      [{ type: 'table' as any, content: { type: 'tableContent', rows: tableRows } } as any],
      block,
      'after'
    );
    editor.focus();
  }, [editor]);

  // --- Text alignment ---
  const setAlignment = useCallback(
    (alignment: 'left' | 'center' | 'right') => {
      if (!editor) return;
      const block = editor.getTextCursorPosition().block;
      editor.updateBlock(block, { props: { textAlignment: alignment } as any });
      editor.focus();
    },
    [editor]
  );

  // --- Indent / Outdent ---
  // BlockNote >= 0.52 changed nestBlock()/unnestBlock() to take no arguments;
  // they operate on the current text-cursor block implicitly.
  const indent = useCallback(() => {
    if (!editor) return;
    editor.nestBlock();
    editor.focus();
  }, [editor]);

  const outdent = useCallback(() => {
    if (!editor) return;
    editor.unnestBlock();
    editor.focus();
  }, [editor]);

  // --- Insert link ---
  const insertLink = useCallback(() => {
    if (!editor) return;
    const url = window.prompt('Enter URL:');
    if (url) {
      editor.createLink(url);
    }
    editor.focus();
  }, [editor]);

  // --- Media upload state ---
  const [uploading, setUploading] = useState(false);
  const [showPageLinkPicker, setShowPageLinkPicker] = useState(false);

  // --- Media upload helpers ---
  const uploadAndInsert = useCallback(
    async (file: File, blockType: 'image' | 'video' | 'file') => {
      if (!editor) return;
      setUploading(true);
      try {
        // Get presigned upload URL from BFF
        const attachment = await pagesApi.createAttachment(pageId, {
          filename: file.name,
          mimeType: file.type,
          size: file.size,
        });

        // Upload directly to S3
        if (attachment.uploadUrl) {
          await fetch(attachment.uploadUrl, {
            method: 'PUT',
            body: file,
            headers: { 'Content-Type': file.type },
          });
        }

        // Use stable BFF redirect path — never expires, auth checked per request
        const url = `/api/v1/pages/${encodeURIComponent(pageId)}/attachments/${encodeURIComponent(attachment.attachmentId)}/download?redirect=true`;

        // Insert the block with the stable URL — BlockNote renders <img>/<video> from this
        const block = editor.getTextCursorPosition().block;
        editor.insertBlocks(
          [{ type: blockType, props: { url, name: file.name, showPreview: true } } as any],
          block,
          'after'
        );

        // Ensure a trailing paragraph exists after the media block so users can click below
        const allBlocks = editor.document;
        const insertedIndex = allBlocks.findIndex(
          (b: any) => b.props?.url === url
        );
        if (insertedIndex >= 0 && insertedIndex === allBlocks.length - 1) {
          editor.insertBlocks(
            [{ type: 'paragraph', content: [] } as any],
            allBlocks[insertedIndex],
            'after'
          );
        }

        editor.focus();
      } catch {
        // Upload failed — show nothing rather than crash
      } finally {
        setUploading(false);
      }
    },
    [editor, pageId]
  );

  const openFilePicker = useCallback(
    (accept: string, blockType: 'image' | 'video' | 'file') => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = accept;
      input.onchange = () => {
        const file = input.files?.[0];
        if (file) uploadAndInsert(file, blockType);
      };
      input.click();
    },
    [uploadAndInsert]
  );

  const insertImage = useCallback(() => openFilePicker('image/*', 'image'), [openFilePicker]);
  const insertVideo = useCallback(() => openFilePicker('video/*', 'video'), [openFilePicker]);
  const insertFile = useCallback(() => openFilePicker('*/*', 'file'), [openFilePicker]);

  // --- Delete current block ---
  const deleteCurrentBlock = useCallback(() => {
    if (!editor) return;
    const block = editor.getTextCursorPosition().block;
    if (block) {
      editor.removeBlocks([block]);
    }
    editor.focus();
  }, [editor]);

  // --- Insert page link ---
  const insertPageLink = useCallback(
    (libraryId: string, pageId: string, title: string) => {
      if (!editor) return;
      const url = `/pages/${libraryId}/${pageId}`;
      // Insert link with the page title as display text
      const selection = editor.getSelection();
      if (selection && selection.blocks.length > 0) {
        // If text is selected, convert it to a link
        editor.createLink(url, title);
      } else {
        // Otherwise insert the title as linked text at cursor
        const cursor = editor.getTextCursorPosition();
        if (cursor?.block) {
          const block = cursor.block;
          const content = Array.isArray(block.content) ? [...block.content] : [];
          content.push({
            type: 'link',
            href: url,
            content: [{ type: 'text', text: title, styles: {} }],
          } as any);
          editor.updateBlock(block, { content } as any);
        }
      }
      editor.focus();
    },
    [editor]
  );

  // --- Insert divider ---
  const insertDivider = useCallback(() => {
    if (!editor) return;
    const block = editor.getTextCursorPosition().block;
    editor.insertBlocks(
      [{ type: 'divider' as any } as any],
      block,
      'after'
    );
    editor.focus();
  }, [editor]);

  const disabled = !editor;

  // Block type dropdown items
  const blockTypeItems: DropdownItem[] = [
    { label: '¶  Paragraph', title: 'Paragraph', onClick: () => setBlockType('paragraph') },
    { label: '❝  Quote', title: 'Block quote', onClick: () => setBlockType('quote') },
    { label: '{ }  Code block', title: 'Code block', onClick: () => setBlockType('codeBlock') },
    { label: '▶  Toggle', title: 'Toggle/collapsible', onClick: () => setBlockType('toggleListItem') },
    { label: '—  Divider', title: 'Horizontal divider', onClick: insertDivider },
  ];

  // Alignment dropdown items
  const alignItems: DropdownItem[] = [
    { label: '⫷  Left', title: 'Align left', onClick: () => setAlignment('left') },
    { label: '⫿  Center', title: 'Align center', onClick: () => setAlignment('center') },
    { label: '⫸  Right', title: 'Align right', onClick: () => setAlignment('right') },
  ];

  return (
    <div
      className="flex items-center gap-0.5 px-3 py-2 border-b bg-card flex-wrap sticky -top-6 z-10"
      role="toolbar"
      aria-label="Editor formatting toolbar"
    >
      {/* Headings */}
      <ToolbarButton label="H1" title="Heading 1" onClick={() => toggleHeading(1)} disabled={disabled} />
      <ToolbarButton label="H2" title="Heading 2" onClick={() => toggleHeading(2)} disabled={disabled} />
      <ToolbarButton label="H3" title="Heading 3" onClick={() => toggleHeading(3)} disabled={disabled} />
      <ToolbarButton label="H4" title="Heading 4" onClick={() => toggleHeading(4)} disabled={disabled} />

      <ToolbarSeparator />

      {/* Inline text styles */}
      <ToolbarButton label={<span className="font-bold">B</span>} title="Bold (Ctrl+B)" onClick={() => toggleStyle('bold')} disabled={disabled} />
      <ToolbarButton label={<span className="italic">I</span>} title="Italic (Ctrl+I)" onClick={() => toggleStyle('italic')} disabled={disabled} />
      <ToolbarButton label={<span className="underline">U</span>} title="Underline (Ctrl+U)" onClick={() => toggleStyle('underline')} disabled={disabled} />
      <ToolbarButton label={<span className="line-through">S</span>} title="Strikethrough" onClick={() => toggleStyle('strike')} disabled={disabled} />
      <ToolbarButton label="<>" title="Inline code" onClick={() => toggleStyle('code')} disabled={disabled} />

      {/* Text and highlight colors */}
      <TextColorDropdown
        buttonLabel="A̲"
        buttonTitle="Text color"
        onSelect={(color) => {
          if (!editor) return;
          if (color === 'default') {
            editor.toggleStyles({ textColor: 'default' } as any);
          } else {
            editor.toggleStyles({ textColor: color } as any);
          }
          editor.focus();
        }}
        disabled={disabled}
      />
      <TextColorDropdown
        buttonLabel="🖍"
        buttonTitle="Highlight color"
        onSelect={(color) => {
          if (!editor) return;
          if (color === 'default') {
            editor.toggleStyles({ backgroundColor: 'default' } as any);
          } else {
            editor.toggleStyles({ backgroundColor: color } as any);
          }
          editor.focus();
        }}
        disabled={disabled}
      />

      <ToolbarSeparator />

      {/* Lists */}
      <ToolbarDropdown
        buttonLabel="List"
        buttonTitle="List type"
        items={[
          { label: '•  Bullet', title: 'Bulleted list', onClick: () => toggleList('bulletListItem') },
          { label: '1.  Numbered', title: 'Numbered list', onClick: () => toggleList('numberedListItem') },
          { label: '☑  Check', title: 'Checklist', onClick: () => toggleList('checkListItem') },
        ]}
        disabled={disabled}
      />

      <ToolbarSeparator />

      {/* Block types (grouped dropdown) */}
      <ToolbarDropdown
        buttonLabel="Block"
        buttonTitle="Block type"
        items={blockTypeItems}
        disabled={disabled}
      />

      {/* Table size picker */}
      <TableSizePicker onSelect={insertTable} disabled={disabled} />

      {/* Alignment (grouped dropdown) */}
      <ToolbarDropdown
        buttonLabel="Align"
        buttonTitle="Text alignment"
        items={alignItems}
        disabled={disabled}
      />

      <ToolbarSeparator />

      {/* Indent / Outdent */}
      <ToolbarButton label="→" title="Indent (Tab)" onClick={indent} disabled={disabled} />
      <ToolbarButton label="←" title="Outdent (Shift+Tab)" onClick={outdent} disabled={disabled} />

      <ToolbarSeparator />

      {/* Insert dropdown — consolidates link, image, video, file, page link actions */}
      <ToolbarDropdown
        buttonLabel="Insert"
        buttonTitle="Insert content"
        items={[
          { label: '🔗  Link (URL)', title: 'Insert link', onClick: insertLink },
          { label: '📄  Page link', title: 'Link to another page', onClick: () => setShowPageLinkPicker(true) },
          { label: '🖼  Image', title: 'Insert image', onClick: insertImage },
          { label: '🎬  Video', title: 'Insert video', onClick: insertVideo },
          { label: '📎  File', title: 'Attach file', onClick: insertFile },
        ]}
        disabled={disabled}
      />
      {/* PageLinkPicker rendered hidden — opened via state */}
      <PageLinkPicker onSelect={insertPageLink} disabled={disabled} externalOpen={showPageLinkPicker} onExternalClose={() => setShowPageLinkPicker(false)} />

      <ToolbarSeparator />

      {/* Upload indicator */}
      {uploading && (
        <span className="text-xs text-muted-foreground animate-pulse ml-1" aria-live="polite">
          Uploading...
        </span>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Presence avatars — other users viewing this page */}
      <PagePresence pageId={pageId} />

      {/* Save indicator and button */}
      <div className="flex items-center gap-2">
        {hasUnsavedChanges && (
          <span className="text-xs text-warning" aria-live="polite">
            Unsaved changes
          </span>
        )}
        {!hasUnsavedChanges && !saving && (
          <span className="text-xs text-success" aria-live="polite">
            Saved
          </span>
        )}
        {saving && (
          <span className="text-xs text-muted-foreground" aria-live="polite">
            Saving...
          </span>
        )}
        <button
          type="button"
          onClick={onSave}
          disabled={!hasUnsavedChanges || saving}
          className={`px-3 py-1 text-sm font-medium rounded transition-colors
            ${hasUnsavedChanges && !saving
              ? 'bg-primary text-primary-foreground hover:bg-primary/90'
              : 'bg-muted text-muted-foreground cursor-not-allowed'}
          `}
          aria-label="Save page"
        >
          Save
        </button>
      </div>
    </div>
  );
}
